import React, { useState, useEffect, useCallback } from 'react';
import { supabaseAdmin } from '../lib/supabase';
import ConfirmModal from '../components/ConfirmModal';
import styles from './TablePage.module.css';

type PublicUser = { id: string; email: string; nickname: string | null; is_active: boolean; is_admin: boolean; created_at: string };
type AuthUser = { id: string; email: string | null; email_confirmed_at: string | null; last_sign_in_at: string | null; created_at: string; raw_app_meta_data: any };

type Tab = 'public' | 'auth' | 'settings';

type UserSetting = {
  user_id: string;
  email?: string;
  daily_alarm_enabled: boolean;
  daily_alarm_time: string;
  daily_goal: number;
  review_algorithm: string;
  study_language: string;
  dark_mode: string;
  font_size: string;
  updated_at: string;
};

export default function UsersPage() {
  const [tab, setTab] = useState<Tab>('public');

  // public.users state
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [loadingPublic, setLoadingPublic] = useState(true);
  const [publicError, setPublicError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editTarget, setEditTarget] = useState<PublicUser | null>(null);
  const [form, setForm] = useState<any>({});
  const [deleteTarget, setDeleteTarget] = useState<PublicUser | null>(null);

  // auth.users state
  const [authUsers, setAuthUsers] = useState<AuthUser[]>([]);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSearch, setAuthSearch] = useState('');
  const [authFetched, setAuthFetched] = useState(false);

  // user_settings state
  const [settings, setSettings] = useState<UserSetting[]>([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsFetched, setSettingsFetched] = useState(false);
  const [editSetting, setEditSetting] = useState<UserSetting | null>(null);
  const [settingForm, setSettingForm] = useState<Partial<UserSetting>>({});
  const [deleteSettingTarget, setDeleteSettingTarget] = useState<UserSetting | null>(null);

  const fetchPublicUsers = useCallback(async () => {
    setLoadingPublic(true);
    setPublicError(null);
    const { data, error } = await supabaseAdmin.from('users').select('*').order('created_at', { ascending: false });
    if (error) setPublicError(error.message);
    setUsers((data as PublicUser[]) ?? []);
    setLoadingPublic(false);
  }, []);

  const fetchAuthUsers = useCallback(async () => {
    setLoadingAuth(true);
    setAuthError(null);
    const { data, error } = await supabaseAdmin.auth.admin.listUsers();
    if (error) setAuthError(error.message);
    const mapped = (data?.users ?? []).map((u: any) => ({
      id: u.id,
      email: u.email ?? null,
      email_confirmed_at: u.email_confirmed_at ?? null,
      last_sign_in_at: u.last_sign_in_at ?? null,
      created_at: u.created_at,
      raw_app_meta_data: u.app_metadata ?? {},
    }));
    setAuthUsers(mapped);
    setLoadingAuth(false);
    setAuthFetched(true);
  }, []);

  const fetchSettings = useCallback(async () => {
    setLoadingSettings(true);
    const { data: settingsData } = await supabaseAdmin
      .from('user_settings')
      .select('*')
      .order('updated_at', { ascending: false });
    const { data: usersData } = await supabaseAdmin.from('users').select('id, email');
    const emailMap: Record<string, string> = {};
    (usersData ?? []).forEach((u: any) => { emailMap[u.id] = u.email; });
    setSettings((settingsData ?? []).map((s: any) => ({ ...s, email: emailMap[s.user_id] ?? '—' })));
    setLoadingSettings(false);
    setSettingsFetched(true);
  }, []);

  useEffect(() => { fetchPublicUsers(); }, [fetchPublicUsers]);

  useEffect(() => {
    if (tab === 'auth' && !authFetched) fetchAuthUsers();
  }, [tab, authFetched, fetchAuthUsers]);

  useEffect(() => {
    if (tab === 'settings' && !settingsFetched) fetchSettings();
  }, [tab, settingsFetched, fetchSettings]);

  async function handleSave() {
    const { error } = await supabaseAdmin.from('users').update({
      nickname: form.nickname,
      is_active: form.is_active,
      is_admin: form.is_admin,
    }).eq('id', editTarget!.id);
    if (error) { alert(error.message); return; }
    setEditTarget(null);
    fetchPublicUsers();
  }

  async function handleSettingSave() {
    if (!editSetting) return;
    const { error } = await supabaseAdmin.from('user_settings').update({
      daily_alarm_enabled: settingForm.daily_alarm_enabled,
      daily_alarm_time: settingForm.daily_alarm_time,
      daily_goal: settingForm.daily_goal,
      review_algorithm: settingForm.review_algorithm,
      study_language: settingForm.study_language,
      dark_mode: settingForm.dark_mode,
      font_size: settingForm.font_size,
      updated_at: new Date().toISOString(),
    }).eq('user_id', editSetting.user_id);
    if (error) { alert(error.message); return; }
    setEditSetting(null);
    setSettingsFetched(false);
    fetchSettings();
  }

  async function handleSettingDelete() {
    if (!deleteSettingTarget) return;
    const { error } = await supabaseAdmin.from('user_settings').delete().eq('user_id', deleteSettingTarget.user_id);
    if (error) { alert(error.message); return; }
    setDeleteSettingTarget(null);
    setSettingsFetched(false);
    fetchSettings();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    // auth.users 삭제 (cascade로 public.users도 삭제됨)
    const { error } = await supabaseAdmin.auth.admin.deleteUser(deleteTarget.id);
    if (error) {
      // auth 삭제 실패 시 public.users만 삭제
      await supabaseAdmin.from('users').delete().eq('id', deleteTarget.id);
    }
    setDeleteTarget(null);
    fetchPublicUsers();
  }

  const filteredPublic = users.filter(u =>
    !search || `${u.email} ${u.nickname ?? ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const filteredAuth = authUsers.filter(u =>
    !authSearch || (u.email ?? '').toLowerCase().includes(authSearch.toLowerCase())
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>👥 사용자 관리</h1>
          <span className={styles.count}>
            {tab === 'public' ? `${filteredPublic.length}명` : tab === 'auth' ? `${filteredAuth.length}명` : `${settings.length}명`}
          </span>
          <div className={styles.dbBadge}>
            {tab === 'public' ? '🗄 public.users' : tab === 'auth' ? '🗄 auth.users (조회 전용)' : '🗄 user_settings'}
          </div>
        </div>
        <div className={styles.actions}>
          <input
            className={`input ${styles.search}`}
            placeholder="이메일 검색..."
            value={tab === 'public' ? search : authSearch}
            onChange={e => tab === 'public' ? setSearch(e.target.value) : setAuthSearch(e.target.value)}
          />
          <button className="btn btn-ghost" onClick={tab === 'public' ? fetchPublicUsers : fetchAuthUsers}>↺</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {([
          { key: 'public', label: '📋 public.users' },
          { key: 'auth',   label: '🔐 auth.users' },
          { key: 'settings', label: '⚙️ user_settings' },
        ] as { key: Tab; label: string }[]).map(({ key, label }) => (
          <button
            key={key}
            className={`btn ${tab === key ? 'btn-primary' : 'btn-ghost'}`}
            style={{ fontSize: 12, padding: '7px 18px' }}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* public.users tab */}
      {tab === 'public' && (
        loadingPublic ? <div className={styles.loading}>로딩 중...</div> :
        publicError ? <div style={{padding:20,color:'#F07880',background:'rgba(240,120,128,0.1)',borderRadius:12,fontSize:13}}>❌ {publicError}<br/><span style={{opacity:0.6,fontSize:11}}>SQL Editor에서 get_all_users() 함수가 생성됐는지 확인하세요.</span></div> : (
          <div className={styles.tableWrapper}>
            <table className="data-table">
              <thead><tr>
                <th>Email</th><th>Nickname</th><th>Status</th><th>Admin</th><th>가입일</th><th style={{width:100}}>Actions</th>
              </tr></thead>
              <tbody>
                {filteredPublic.map(u => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>
                      {u.nickname
                        ? <strong>{u.nickname}</strong>
                        : <span style={{color:'rgba(255,255,255,0.25)',fontStyle:'italic',fontWeight:400}}>없음</span>}
                    </td>
                    <td>
                      <span style={{
                        background: u.is_active ? 'rgba(52,201,138,0.15)' : 'rgba(240,120,128,0.15)',
                        color: u.is_active ? '#34C98A' : '#F07880',
                        border: `1px solid ${u.is_active ? 'rgba(52,201,138,0.4)' : 'rgba(240,120,128,0.4)'}`,
                        borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                      }}>
                        {u.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </td>
                    <td>
                      {u.is_admin && (
                        <span style={{
                          background: 'rgba(232,100,154,0.2)', color: '#E8649A',
                          border: '1px solid rgba(232,100,154,0.4)',
                          borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                        }}>ADMIN</span>
                      )}
                    </td>
                    <td style={{color:'rgba(255,255,255,0.5)',fontSize:12}}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('ko-KR') : '-'}
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button className="btn btn-ghost" style={{padding:'4px 10px',fontSize:12}}
                          onClick={() => { setEditTarget(u); setForm({nickname: u.nickname, is_active: u.is_active, is_admin: u.is_admin}); }}>편집</button>
                        <button className="btn btn-danger" style={{padding:'4px 10px',fontSize:12}}
                          onClick={() => setDeleteTarget(u)}>삭제</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* auth.users tab */}
      {tab === 'auth' && (
        loadingAuth ? <div className={styles.loading}>로딩 중...</div> :
        authError ? <div style={{padding:20,color:'#F07880',background:'rgba(240,120,128,0.1)',borderRadius:12,fontSize:13}}>❌ {authError}<br/><span style={{opacity:0.6,fontSize:11}}>.env 파일에 REACT_APP_SUPABASE_SERVICE_ROLE_KEY를 설정하고 npm start를 재시작하세요.</span></div> : (
          <div className={styles.tableWrapper}>
            <table className="data-table">
              <thead><tr>
                <th>ID</th><th>Email</th><th>이메일 인증</th><th>마지막 로그인</th><th>Provider</th><th>가입일</th>
              </tr></thead>
              <tbody>
                {filteredAuth.map(u => (
                  <tr key={u.id}>
                    <td style={{fontSize:11,color:'rgba(255,255,255,0.4)',fontFamily:'monospace'}}>{u.id}</td>
                    <td>{u.email ?? '—'}</td>
                    <td>
                      <span style={{
                        background: u.email_confirmed_at ? 'rgba(52,201,138,0.15)' : 'rgba(240,120,128,0.15)',
                        color: u.email_confirmed_at ? '#34C98A' : '#F07880',
                        border: `1px solid ${u.email_confirmed_at ? 'rgba(52,201,138,0.4)' : 'rgba(240,120,128,0.4)'}`,
                        borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                      }}>
                        {u.email_confirmed_at ? '인증됨' : '미인증'}
                      </span>
                    </td>
                    <td style={{color:'rgba(255,255,255,0.5)',fontSize:12}}>
                      {u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString('ko-KR', {month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'}) : '—'}
                    </td>
                    <td>
                      {(() => {
                        const provider = u.raw_app_meta_data?.provider ?? '—';
                        return (
                          <span style={{
                            background: 'rgba(155,127,212,0.15)', color: '#9B7FD4',
                            border: '1px solid rgba(155,127,212,0.3)',
                            borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                          }}>{provider}</span>
                        );
                      })()}
                    </td>
                    <td style={{color:'rgba(255,255,255,0.5)',fontSize:12}}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('ko-KR') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* user_settings tab */}
      {tab === 'settings' && (
        loadingSettings ? <div className={styles.loading}>로딩 중...</div> : (
          <div className={styles.tableWrapper}>
            <table className="data-table">
              <thead><tr>
                <th>Email</th>
                <th>알림</th>
                <th>알림 시간</th>
                <th>일일 목표</th>
                <th>복습 알고리즘</th>
                <th>학습 언어</th>
                <th>다크 모드</th>
                <th>글자 크기</th>
                <th>업데이트</th>
                <th style={{width:100}}>Actions</th>
              </tr></thead>
              <tbody>
                {settings.length === 0
                  ? <tr><td colSpan={10} style={{textAlign:'center',color:'rgba(255,255,255,0.3)',padding:40}}>설정 데이터 없음</td></tr>
                  : settings.map(s => (
                  <tr key={s.user_id}>
                    <td style={{color:'rgba(255,255,255,0.7)',fontSize:12}}>{s.email}</td>
                    <td>
                      <span style={{
                        background: s.daily_alarm_enabled ? 'rgba(52,201,138,0.15)' : 'rgba(255,255,255,0.07)',
                        color: s.daily_alarm_enabled ? '#34C98A' : 'rgba(255,255,255,0.4)',
                        border: `1px solid ${s.daily_alarm_enabled ? 'rgba(52,201,138,0.35)' : 'rgba(255,255,255,0.15)'}`,
                        borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                      }}>
                        {s.daily_alarm_enabled ? 'ON' : 'OFF'}
                      </span>
                    </td>
                    <td style={{color:'rgba(255,255,255,0.65)',fontSize:12,fontFamily:'monospace'}}>{s.daily_alarm_time ?? '—'}</td>
                    <td style={{color:'rgba(255,255,255,0.65)',fontSize:12}}>{s.daily_goal}문제</td>
                    <td>
                      <span style={{background:'rgba(155,127,212,0.15)',color:'#C4A8FF',border:'1px solid rgba(155,127,212,0.3)',borderRadius:6,padding:'2px 10px',fontSize:11,fontWeight:700}}>
                        {s.review_algorithm}
                      </span>
                    </td>
                    <td style={{color:'rgba(255,255,255,0.65)',fontSize:12}}>{s.study_language}</td>
                    <td>
                      <span style={{background:'rgba(255,255,255,0.08)',color:'rgba(255,255,255,0.6)',borderRadius:6,padding:'2px 10px',fontSize:11,fontWeight:700}}>
                        {s.dark_mode}
                      </span>
                    </td>
                    <td style={{color:'rgba(255,255,255,0.65)',fontSize:12}}>{s.font_size}</td>
                    <td style={{color:'rgba(255,255,255,0.35)',fontSize:11}}>
                      {s.updated_at ? new Date(s.updated_at).toLocaleString('ko-KR',{month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'}) : '—'}
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button className="btn btn-ghost" style={{padding:'4px 10px',fontSize:12}}
                          onClick={() => { setEditSetting(s); setSettingForm({...s}); }}>편집</button>
                        <button className="btn btn-danger" style={{padding:'4px 10px',fontSize:12}}
                          onClick={() => setDeleteSettingTarget(s)}>삭제</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Edit modal (public.users only) */}
      {editTarget && (
        <div className="modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
              <h2 style={{fontSize:18,fontWeight:800}}>사용자 편집</h2>
              <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={() => setEditTarget(null)}>✕</button>
            </div>
            <div style={{marginBottom:8,fontSize:13,color:'rgba(255,255,255,0.5)'}}>{editTarget.email}</div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>Nickname</label>
              <input className="input" value={form.nickname ?? ''} onChange={e => setForm((p: any) => ({...p, nickname: e.target.value}))} />
            </div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>Status</label>
              <div style={{display:'flex',gap:10}}>
                {[true, false].map(v => (
                  <button key={String(v)} className={`btn ${form.is_active === v ? 'btn-primary' : 'btn-ghost'}`}
                    style={{flex:1}} onClick={() => setForm((p: any) => ({...p, is_active: v}))}>
                    {v ? 'ACTIVE' : 'INACTIVE'}
                  </button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>관리자 권한</label>
              <div style={{display:'flex',gap:10}}>
                {[true, false].map(v => (
                  <button key={String(v)}
                    className={`btn ${form.is_admin === v ? (v ? 'btn-primary' : 'btn-ghost') : 'btn-ghost'}`}
                    style={{flex:1, ...(form.is_admin === v && v ? {background:'linear-gradient(135deg,#E8649A,#C84080)'} : {})}}
                    onClick={() => setForm((p: any) => ({...p, is_admin: v}))}>
                    {v ? '🔑 ADMIN' : '일반 사용자'}
                  </button>
                ))}
              </div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={() => setEditTarget(null)}>취소</button>
              <button className="btn btn-primary" onClick={handleSave}>저장</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal title="사용자 삭제" message={`"${deleteTarget.email}" 계정을 삭제할까요? 되돌릴 수 없습니다.`}
          onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}

      {/* user_settings 편집 모달 */}
      {editSetting && (
        <div className="modal-overlay" onClick={() => setEditSetting(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
              <h2 style={{fontSize:18,fontWeight:800}}>설정 편집</h2>
              <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={() => setEditSetting(null)}>✕</button>
            </div>
            <div style={{marginBottom:16,fontSize:13,color:'rgba(255,255,255,0.5)'}}>{editSetting.email}</div>

            {[
              { label: '알림 활성화', key: 'daily_alarm_enabled', type: 'toggle' },
              { label: '알림 시간', key: 'daily_alarm_time', type: 'time' },
              { label: '일일 목표 (문제 수)', key: 'daily_goal', type: 'number' },
              { label: '복습 알고리즘', key: 'review_algorithm', type: 'select',
                options: ['normal', 'strict', 'random'] },
              { label: '학습 언어', key: 'study_language', type: 'select',
                options: ['ko_en', 'en_ko'] },
              { label: '다크 모드', key: 'dark_mode', type: 'select',
                options: ['auto', 'on', 'off'] },
              { label: '글자 크기', key: 'font_size', type: 'select',
                options: ['small', 'medium', 'large'] },
            ].map(({ label, key, type, options }) => (
              <div key={key} style={{marginBottom:14}}>
                <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>
                  {label}
                </label>
                {type === 'toggle' ? (
                  <div style={{display:'flex',gap:10}}>
                    {[true,false].map(v => (
                      <button key={String(v)}
                        className={`btn ${(settingForm as any)[key] === v ? 'btn-primary' : 'btn-ghost'}`}
                        style={{flex:1}}
                        onClick={() => setSettingForm(p => ({...p,[key]:v}))}>
                        {v ? 'ON' : 'OFF'}
                      </button>
                    ))}
                  </div>
                ) : type === 'select' ? (
                  <select className="input"
                    value={(settingForm as any)[key] ?? ''}
                    onChange={e => setSettingForm(p => ({...p,[key]:e.target.value}))}>
                    {options!.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : type === 'number' ? (
                  <input className="input" type="number"
                    value={(settingForm as any)[key] ?? ''}
                    onChange={e => setSettingForm(p => ({...p,[key]:Number(e.target.value)}))} />
                ) : (
                  <input className="input" type="text"
                    value={(settingForm as any)[key] ?? ''}
                    onChange={e => setSettingForm(p => ({...p,[key]:e.target.value}))} />
                )}
              </div>
            ))}

            <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={() => setEditSetting(null)}>취소</button>
              <button className="btn btn-primary" onClick={handleSettingSave}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* user_settings 삭제 확인 모달 */}
      {deleteSettingTarget && (
        <ConfirmModal
          title="설정 삭제"
          message={`"${deleteSettingTarget.email}"의 설정을 삭제할까요? 앱에서 기본값으로 초기화됩니다.`}
          onConfirm={handleSettingDelete}
          onCancel={() => setDeleteSettingTarget(null)}
        />
      )}
    </div>
  );
}
